import React, { Fragment, useCallback } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { get } from 'lodash';
import Button from 'reactstrap/lib/Button';
import { useToggle } from 'site-modules/shared/hooks/use-toggle';
import { EventToolbox } from 'client/utils/event-toolbox';
import { TrackingConstant } from 'client/tracking/constant';
import { InventoryTrimSummaryEntities } from 'client/data/models/inventory-core-configurations';
import { VehicleEntities } from 'client/data/models/vehicle-v2';
import { VisitorEntities } from 'client/data/models/visitor';
import { DEFAULT_TRIM_CTA_NAME } from 'site-modules/shared/constants/new-core';
import { LEAD_FORMS_CREATIVE_ID } from 'site-modules/shared/components/lead-form/unified-lead-form/constants/creative-ids';
import { getPriceString } from 'site-modules/shared/utils/price-utils';
import { getLeadFormCreativeId } from 'site-modules/shared/components/lead-form/utils/lead-form-creative-id';
import { isUnknownZip } from 'site-modules/shared/utils/location';
import { getEspLabel, getSuggestedDisplayPrice } from 'site-modules/shared/utils/core-page/suggested-price';
import { ShoppingLink } from 'site-modules/shared/components/shopping-link/shopping-link';
import { DockedContainer } from 'site-modules/shared/components/docked-container/docked-container';
import { TransparentPricingLeadFormDrawer } from 'site-modules/shared/components/lead-form/transparent-pricing-lead-form/transparent-pricing-lead-form-drawer';

import './docked-lead-form-button.scss';

export function DockedLeadFormButtonUI({
  currentTrim,
  vehicle,
  visitorLocation,
  showElementSelector,
  creativeId,
  pageName,
  showLeadForm,
  fallbackFragment,
  isMobile,
  core6958Recipe,
}) {
  const [isLeadFormOpen, toggleLeadFormOpen] = useToggle(false);
  const isInternational = isUnknownZip(visitorLocation);
  const { zipCode, city } = visitorLocation;
  const { styleId, trimName, pricing } = currentTrim;

  const totalMsrp = pricing?.totalMsrp;
  const baseMsrp = pricing?.baseMsrp;
  const suggestedPrice = pricing?.suggestedPrice;
  const nationalBasePriceTmv = isInternational ? pricing?.pricing?.nationalBasePrice?.tmv : null;
  const displayPrice = getSuggestedDisplayPrice({ nationalBasePriceTmv, suggestedPrice, totalMsrp, baseMsrp });
  const espLabel = getEspLabel({ suggestedPrice, totalMsrp, nationalBasePriceTmv });
  const ctaLabel = isInternational ? 'See Local Pricing' : DEFAULT_TRIM_CTA_NAME;

  const leadFormCreativeId = getLeadFormCreativeId({
    isNewVin: true,
    pageNameLegacy: pageName,
    formName: LEAD_FORMS_CREATIVE_ID.NEW_DOCK_BOTTOM,
  });

  const onDisplayChange = useCallback(
    isDisplayed => {
      if (isDisplayed) {
        EventToolbox.fireTrackAction({
          event_type: TrackingConstant.EVENT_TYPE_WIDGET_VIEW,
          event_data: {
            action_name: TrackingConstant.ACTION_WIDGET_VIEW,
            action_cause: TrackingConstant.ACTION_CAUSE_SCROLL,
            creative_id: creativeId,
          },
        });
      }
    },
    [creativeId]
  );

  return (
    <Fragment>
      {showLeadForm ? (
        <DockedContainer
          className={core6958Recipe === 'chal2' ? 'pos-r bg-blue-50 px-0_5 py-0_75' : undefined}
          onDisplayChange={onDisplayChange}
          showElementSelector={showElementSelector}
          creativeId={creativeId}
          isAfterElement
        >
          {core6958Recipe === 'chal1' ? (
            <ShoppingLink
              linkTag="button"
              onClick={toggleLeadFormOpen}
              label={ctaLabel}
              data-tracking-id="view_pricing_details"
              creativeId={creativeId}
              className="docked-lead-form-button px-1 py-0_75 bg-white rounded"
              labelClassName="text-gray-darkest fw-bold border-0 bg-transparent"
              iconClassName="icon-price-tags2"
              iconStyles={{ height: '40px', width: '40px' }}
            />
          ) : (
            <div className="d-flex align-items-center">
              <div className="text-white text-nowrap pe-1">
                <div className="small mb-0_25">{espLabel}</div>
                <span className="fw-bold size-24">{getPriceString(displayPrice)}</span>
              </div>
              <Button
                onClick={toggleLeadFormOpen}
                color="outline-blue-50"
                className="docked-lead-form-button click-outside w-100 px-0_5 py-0_5 size-16 bg-white text-transform-none"
                data-tracking-id="view_pricing_details"
              >
                {ctaLabel}
              </Button>
            </div>
          )}
        </DockedContainer>
      ) : (
        fallbackFragment
      )}

      <TransparentPricingLeadFormDrawer
        isOpen={isLeadFormOpen}
        onDrawerToggle={toggleLeadFormOpen}
        params={{
          styleId,
          trim: trimName,
          vehicle,
          zipCode,
          city,
        }}
        options={{
          isMobile,
          bestDealPrice: displayPrice,
          creativeId: leadFormCreativeId,
        }}
        creativeId={leadFormCreativeId}
      />
    </Fragment>
  );
}

DockedLeadFormButtonUI.propTypes = {
  currentTrim: InventoryTrimSummaryEntities.TrimSummary.isRequired,
  vehicle: VehicleEntities.MakeModelSubmodelYear.isRequired,
  visitorLocation: VisitorEntities.Location,
  showElementSelector: PropTypes.string,
  creativeId: PropTypes.string,
  pageName: PropTypes.string,
  fallbackFragment: PropTypes.node,
  showLeadForm: PropTypes.bool,
  isMobile: PropTypes.bool,
  core6958Recipe: PropTypes.string,
};

DockedLeadFormButtonUI.defaultProps = {
  visitorLocation: null,
  showElementSelector: null,
  creativeId: null,
  pageName: null,
  fallbackFragment: null,
  showLeadForm: false,
  isMobile: false,
  core6958Recipe: 'chal1',
};

const mapStateToProps = state => ({
  isMobile: state.mobile,
  visitorLocation: state?.visitor?.location,
  pageName: get(state, 'pageContext.legacy.pageName') || get(state, 'pageContext.page.name'),
});

export const DockedLeadFormButton = connect(mapStateToProps)(DockedLeadFormButtonUI);
